import { useEffect, useId, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { FormCard, PrimaryButton, ChevronRightIcon } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import { buildClaimIncidentHref, formatClaimPolicySummaryLine, formatIncidentRailSummary } from '../claims/claimFlowModel'
import { persistWindscreenDescription, readClaimDraft } from '../claims/claimDraftStorage'
import {
  claimFormFieldClass,
  ClaimFormFieldError,
  ClaimFormSubmitSummaryAlert,
  useClaimFormSubmitAttempt,
} from '../claims/claimFormValidationFeedback'

const MAX_DESCRIPTION_LENGTH = 1000

const DESCRIPTION_TEXTAREA_CLASS =
  'min-h-[192px] w-full resize-y rounded-xl border border-solid border-border-primary bg-background-primary px-[16px] py-[14px] font-body text-sm leading-sm text-text-primary-900 shadow-xs placeholder:text-text-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2'

const INCLUDE_PROMPTS = [
  'Any additional damage',
  'Where you were going to and from',
  'The traffic and/or road conditions',
  'Environmental conditions such as weather',
  'Events leading up to the incident.',
] as const

/**
 * Windscreen/glass branch: user describes the incident (character-limited, persisted to session draft).
 */
export default function ClaimWindscreenStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const descriptionFieldId = useId()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  const [description, setDescription] = useState(() => readClaimDraft()?.windscreenDescription ?? '')

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      persistWindscreenDescription(policyRef, description)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [description, policyRef])

  const backToIncidentStep = () => {
    persistWindscreenDescription(policyRef, description)
    const path = buildClaimIncidentHref(policyRef, typeInQuery, 'windscreen')
    if (navState?.policy) {
      navigate(path, { state: { policy: navState.policy } })
    } else {
      navigate(path)
    }
  }

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const canProceed = description.trim().length > 0
  const { submitAttempted, showSubmitSummary, attemptSubmit, clearSubmitAttempt } =
    useClaimFormSubmitAttempt(canProceed)
  const descriptionFieldError = submitAttempted && !description.trim()

  const handleNext = () => {
    attemptSubmit(() => {
      persistWindscreenDescription(policyRef, description)
      const p = new URLSearchParams()
      if (policyRef) p.set('policyRef', policyRef)
      if (typeInQuery) p.set('type', typeInQuery)
      const search = p.toString()
      const path = search ? `/claims/windscreen/detail?${search}` : '/claims/windscreen/detail'
      navigate(path, {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    })
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={backToIncidentStep}
        title="Describe what happened"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="windscreen"
            incidentSummary={formatIncidentRailSummary(readClaimDraft())}
            windscreenGstRegistered={readClaimDraft()?.windscreenGstRegistered ?? null}
            windscreenAbn={readClaimDraft()?.windscreenAbn ?? null}
            windscreenItcEntitled={readClaimDraft()?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={readClaimDraft()?.windscreenItcPercentage ?? null}
            windscreenReplacedAlready={readClaimDraft()?.windscreenReplacedAlready ?? null}
            windscreenDescription={description}
          />
        }
      >
        <FormCard>
          <section className="flex w-full flex-col items-start gap-[20px]">
            {showSubmitSummary ? <ClaimFormSubmitSummaryAlert /> : null}
            <p className="m-0 font-body text-sm leading-sm text-text-primary-900">
              Provide as much detail as you can about the incident, things to include:
            </p>
            <ul className="m-0 flex list-disc flex-col gap-[8px] pl-[20px] font-body text-sm leading-sm text-text-secondary-700 marker:text-text-secondary-700">
              {INCLUDE_PROMPTS.map((item) => (
                <li key={item} className="pl-[4px]">
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex w-full flex-col items-stretch gap-[8px]">
              <label htmlFor={descriptionFieldId} className="sr-only">
                Incident description (maximum {MAX_DESCRIPTION_LENGTH} characters)
              </label>
              <textarea
                id={descriptionFieldId}
                value={description}
                maxLength={MAX_DESCRIPTION_LENGTH}
                onChange={(e) => {
                  clearSubmitAttempt()
                  setDescription(e.target.value)
                }}
                placeholder="Enter a description..."
                rows={8}
                aria-invalid={descriptionFieldError}
                aria-describedby={descriptionFieldError ? `${descriptionFieldId}-error` : undefined}
                className={claimFormFieldClass(DESCRIPTION_TEXTAREA_CLASS, descriptionFieldError)}
              />
              {descriptionFieldError ? (
                <ClaimFormFieldError id={`${descriptionFieldId}-error`}>
                  Enter a description to continue.
                </ClaimFormFieldError>
              ) : null}
              <p
                className="m-0 self-end font-body text-xs leading-xs text-text-tertiary-600"
                aria-live="polite"
              >
                {description.length}/{MAX_DESCRIPTION_LENGTH} characters
              </p>
            </div>
          </section>
        </FormCard>

        <div className="sticky bottom-0 w-full bg-background-secondary pt-[8px] pb-[8px] md:static md:py-0">
          <PrimaryButton
            type="button"
            onClick={handleNext}
            size="lg"
            fullWidth
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label="Next"
          >
            Next
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
